import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlock, faEnvelope, faKey, faLock } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { AuthModalStep } from '@/types/auth';
import { OtpInput } from '@/components/ui/otp-input';
import { useResendCooldown } from '@/hooks/useResendCooldown';

type AuthModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultTab?: 'login' | 'register';
  websiteUrl?: string;
};

const AuthModal: React.FC<AuthModalProps> = ({ open, onOpenChange, onSuccess, defaultTab = 'register' }) => {
  const { signIn, signUp, verifyOtp, resendOtp, requestPasswordReset, updatePassword } = useAuth();

  const [step, setStep] = useState<AuthModalStep>(AuthModalStep.Register);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { canResend, secondsRemaining, startCooldown, resetCooldown } = useResendCooldown();

  useEffect(() => {
    if (open) {
      setStep(defaultTab === 'login' ? AuthModalStep.Login : AuthModalStep.Register);
      setEmail('');
      setPassword('');
      setFullName('');
      setOtpValue('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setSuccessMessage(null);
      resetCooldown();
    }
  }, [open, defaultTab, resetCooldown]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await signIn(email, password);
    if (!res.success) {
      setError(res.message || 'Authentication failed');
      setLoading(false);
      return;
    }

    onOpenChange(false);
    onSuccess?.();
    setLoading(false);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await signUp(email, password, fullName || undefined);
    if (!res.success) {
      setError(res.message);
      setLoading(false);
      return;
    }

    if (res.requiresVerification) {
      setStep(AuthModalStep.VerifyEmail);
      startCooldown();
    } else {
      onOpenChange(false);
      onSuccess?.();
    }
    setLoading(false);
  };

  const handleVerifyEmail = async () => {
    setLoading(true);
    setError(null);

    const res = await verifyOtp(email, otpValue, 'signup');
    if (!res.success) {
      setError(res.message);
      setLoading(false);
      return;
    }

    onOpenChange(false);
    onSuccess?.();
    setLoading(false);
  };

  const handleResendOtp = async (type: 'signup' | 'recovery') => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const res = await resendOtp(email, type);
    if (!res.success) {
      setError(res.message);
    } else {
      setSuccessMessage(res.message);
      startCooldown();
    }
    setLoading(false);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await requestPasswordReset(email);
    if (!res.success) {
      setError(res.message);
      setLoading(false);
      return;
    }

    setStep(AuthModalStep.VerifyReset);
    startCooldown();
    setLoading(false);
  };

  const handleVerifyReset = async () => {
    setLoading(true);
    setError(null);

    const res = await verifyOtp(email, otpValue, 'recovery');
    if (!res.success) {
      setError(res.message);
      setLoading(false);
      return;
    }

    setStep(AuthModalStep.NewPassword);
    setOtpValue('');
    setLoading(false);
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    const res = await updatePassword(newPassword);
    if (!res.success) {
      setError(res.message);
      setLoading(false);
      return;
    }

    setStep(AuthModalStep.Login);
    setSuccessMessage('Password updated successfully. Please sign in.');
    setNewPassword('');
    setConfirmPassword('');
    setLoading(false);
  };

  const renderLoginStep = () => (
    <form onSubmit={handleLoginSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-5">
        <FontAwesomeIcon icon={faUnlock} className="w-5 h-5 text-foreground" />
        <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
          SIGN IN TO SEE WHAT AI SAYS ABOUT YOUR BUSINESS
        </h2>
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
            EMAIL ADDRESS
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@gmail.com"
            required
            autoComplete="email"
            className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex flex-col gap-3">
          <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
            PASSWORD
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            required
            minLength={6}
            autoComplete="current-password"
            className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
            {loading ? 'PLEASE WAIT...' : 'SIGN IN'}
          </span>
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="animate-fade-in-up">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="animate-fade-in-up border-green-200 bg-green-50">
          <AlertDescription className="text-sm text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.ForgotPassword);
            setError(null);
            setSuccessMessage(null);
          }}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          FORGOT YOUR PASSWORD?
        </button>
      </div>

      <p className="font-mono font-medium text-[10px] text-foreground opacity-50 text-center tracking-[0.5px] uppercase">
        BY CONTINUING YOU AGREE TO OUR{' '}
        <Link to="/terms" className="underline hover:opacity-100">
          SERVICE TERMS
        </Link>
        {' '}AND{' '}
        <Link to="/privacy" className="underline hover:opacity-100">
          PRIVACY POLICY
        </Link>
      </p>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.Register);
            setError(null);
            setSuccessMessage(null);
          }}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          DON'T HAVE AN ACCOUNT? <span className="underline">CREATE ONE</span>
        </button>
      </div>
    </form>
  );

  const renderRegisterStep = () => (
    <form onSubmit={handleRegisterSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-5">
        <FontAwesomeIcon icon={faUnlock} className="w-5 h-5 text-foreground" />
        <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
          CREATE AN ACCOUNT TO LEARN WHAT AI SAYS ABOUT YOUR BUSINESS
        </h2>
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
            FULL NAME (OPTIONAL)
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex flex-col gap-3">
          <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
            EMAIL ADDRESS
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@gmail.com"
            required
            autoComplete="email"
            className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex flex-col gap-3">
          <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
            PASSWORD
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            required
            minLength={6}
            autoComplete="new-password"
            className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
            {loading ? 'PLEASE WAIT...' : 'CREATE ACCOUNT'}
          </span>
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="animate-fade-in-up">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <p className="font-mono font-medium text-[10px] text-foreground opacity-50 text-center tracking-[0.5px] uppercase">
        BY CONTINUING YOU AGREE TO OUR{' '}
        <Link to="/terms" className="underline hover:opacity-100">
          SERVICE TERMS
        </Link>
        {' '}AND{' '}
        <Link to="/privacy" className="underline hover:opacity-100">
          PRIVACY POLICY
        </Link>
      </p>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.Login);
            setError(null);
          }}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          ALREADY HAVE AN ACCOUNT? <span className="underline">SIGN IN</span>
        </button>
      </div>
    </form>
  );

  const renderVerifyEmailStep = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-5">
        <FontAwesomeIcon icon={faEnvelope} className="w-5 h-5 text-foreground" />
        <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
          VERIFY YOUR EMAIL
        </h2>
        <p className="text-sm text-muted-foreground text-center">
          We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      <OtpInput
        length={6}
        value={otpValue}
        onChange={setOtpValue}
        disabled={loading}
        autoFocus={true}
      />

      <button
        type="button"
        onClick={handleVerifyEmail}
        disabled={loading || otpValue.length !== 6}
        className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
          {loading ? 'VERIFYING...' : 'VERIFY EMAIL'}
        </span>
      </button>

      {error && (
        <Alert variant="destructive" className="animate-fade-in-up">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="animate-fade-in-up border-green-200 bg-green-50">
          <AlertDescription className="text-sm text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={() => handleResendOtp('signup')}
          disabled={!canResend || loading}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity disabled:opacity-30"
        >
          {canResend
            ? "DIDN'T RECEIVE THE CODE? RESEND"
            : `RESEND CODE IN ${secondsRemaining}S`}
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.Register);
            setOtpValue('');
            setError(null);
            setSuccessMessage(null);
            resetCooldown();
          }}
          disabled={loading}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          BACK TO REGISTRATION
        </button>
      </div>
    </div>
  );

  const renderForgotPasswordStep = () => (
    <form onSubmit={handleForgotPasswordSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-5">
        <FontAwesomeIcon icon={faKey} className="w-5 h-5 text-foreground" />
        <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
          RESET YOUR PASSWORD
        </h2>
        <p className="text-sm text-muted-foreground text-center">
          Enter your email and we'll send you a reset code
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
          EMAIL ADDRESS
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="john@gmail.com"
          required
          autoComplete="email"
          className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
          {loading ? 'SENDING...' : 'SEND RESET CODE'}
        </span>
      </button>

      {error && (
        <Alert variant="destructive" className="animate-fade-in-up">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.Login);
            setError(null);
          }}
          disabled={loading}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          BACK TO LOGIN
        </button>
      </div>
    </form>
  );

  const renderVerifyResetStep = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-5">
        <FontAwesomeIcon icon={faKey} className="w-5 h-5 text-foreground" />
        <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
          ENTER RESET CODE
        </h2>
        <p className="text-sm text-muted-foreground text-center">
          We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      <OtpInput
        length={6}
        value={otpValue}
        onChange={setOtpValue}
        disabled={loading}
        autoFocus={true}
      />

      <button
        type="button"
        onClick={handleVerifyReset}
        disabled={loading || otpValue.length !== 6}
        className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
          {loading ? 'VERIFYING...' : 'VERIFY CODE'}
        </span>
      </button>

      {error && (
        <Alert variant="destructive" className="animate-fade-in-up">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="animate-fade-in-up border-green-200 bg-green-50">
          <AlertDescription className="text-sm text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={() => handleResendOtp('recovery')}
          disabled={!canResend || loading}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity disabled:opacity-30"
        >
          {canResend
            ? "DIDN'T RECEIVE THE CODE? RESEND"
            : `RESEND CODE IN ${secondsRemaining}S`}
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setStep(AuthModalStep.ForgotPassword);
            setOtpValue('');
            setError(null);
            setSuccessMessage(null);
            resetCooldown();
          }}
          disabled={loading}
          className="font-mono font-medium text-xs text-foreground opacity-50 hover:opacity-100 transition-opacity"
        >
          BACK
        </button>
      </div>
    </div>
  );

  const renderNewPasswordStep = () => {
    const passwordsMatch = newPassword === confirmPassword;
    const isValid = newPassword.length >= 6 && (confirmPassword.length === 0 || passwordsMatch);

    return (
      <form onSubmit={handleSetNewPassword} className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-5">
          <FontAwesomeIcon icon={faLock} className="w-5 h-5 text-foreground" />
          <h2 className="font-condensed font-bold text-2xl uppercase text-center leading-tight">
            SET NEW PASSWORD
          </h2>
        </div>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
              NEW PASSWORD
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
              autoComplete="new-password"
              className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex flex-col gap-3">
            <label className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50">
              CONFIRM PASSWORD
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
              className="w-full h-12 border border-border rounded px-3 font-sans text-base text-foreground placeholder:opacity-50 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-destructive">Passwords do not match</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !isValid || !passwordsMatch}
          className="w-full bg-primary h-14 flex items-center justify-center rounded hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
            {loading ? 'UPDATING...' : 'SET NEW PASSWORD'}
          </span>
        </button>

        {error && (
          <Alert variant="destructive" className="animate-fade-in-up">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}
      </form>
    );
  };

  const renderStep = () => {
    switch (step) {
      case AuthModalStep.Login:
        return renderLoginStep();
      case AuthModalStep.Register:
        return renderRegisterStep();
      case AuthModalStep.VerifyEmail:
        return renderVerifyEmailStep();
      case AuthModalStep.ForgotPassword:
        return renderForgotPasswordStep();
      case AuthModalStep.VerifyReset:
        return renderVerifyResetStep();
      case AuthModalStep.NewPassword:
        return renderNewPasswordStep();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[512px] px-5 py-8 sm:px-10 sm:py-12 bg-white border border-white/80 shadow-lg">
        {renderStep()}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
